import { useState } from 'react';
import { API_BASE_URL } from '../config';
import { useNavigate, Link } from 'react-router-dom';
import { authFetch } from '../utils/auth';
import { useAuth } from '../context/AuthContext';
import './../assets/css/starting.css';
import deFlag from './../assets/images/de.svg';
import mxFlag from './../assets/images/mx.svg';
import frFlag from './../assets/images/fr.svg';
import ruFlag from './../assets/images/ru.svg';
import brFlag from './../assets/images/br.svg';

const languages = [
    { id: 0, name: "german", displayName: "German", image: deFlag },
    { id: 1, name: "mexican spanish", displayName: "Mexican Spanish", image: mxFlag },
    { id: 2, name: "french", displayName: "French", image: frFlag },
    { id: 3, name: "russian", displayName: "Russian", image: ruFlag },
    { id: 4, name: "brazilian portuguese", displayName: "Brazilian Portuguese", image: brFlag }
];

const months = [
    { value: "01", name: "Jan" },
    { value: "02", name: "Feb" },
    { value: "03", name: "Mar" },
    { value: "04", name: "Apr" },
    { value: "05", name: "May" },
    { value: "06", name: "Jun" },
    { value: "07", name: "Jul" },
    { value: "08", name: "Aug" },
    { value: "09", name: "Sep" },
    { value: "10", name: "Oct" },
    { value: "11", name: "Nov" },
    { value: "12", name: "Dec" }
];

function GetStarted() {
    const navigate = useNavigate();
    const { login } = useAuth();

    // Step state
    const [currentStep, setCurrentStep] = useState(1);
    const [formError, setFormError] = useState("");
    const [formErrorCode, setFormErrorCode] = useState("");
    const [loading, setLoading] = useState(false);

    // Step 1: Language selection
    const [selections, setSelections] = useState([false, false, false, false, false]);

    // Step 2: Personal info (controlled to prevent losing inputs on back/forward)
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [dobDay, setDobDay] = useState("");
    const [dobMonth, setDobMonth] = useState("");
    const [dobYear, setDobYear] = useState("");

    // Step 3: Password & Terms
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [termsAgreed, setTermsAgreed] = useState(false);

    const toggleLanguage = (id) => {
        const next = [...selections];
        next[id] = !next[id];
        setSelections(next);
        setFormError("");
    };

    const handleFirstNext = (e) => {
        e.preventDefault();
        const selectedCount = selections.filter(Boolean).length;
        if (selectedCount === 0) {
            setFormError("Please select at least one language to learn.");
            return;
        }
        setFormError("");
        setCurrentStep(2);
    };

    const handleSecondNext = (e) => {
        e.preventDefault();

        const trimmedFirst = firstName.trim();
        const trimmedLast = lastName.trim();
        const trimmedEmail = email.trim();

        if (!trimmedFirst || !trimmedLast || !trimmedEmail || !dobDay || !dobMonth || !dobYear) {
            setFormError("Please fill out all fields.");
            return;
        }

        if (trimmedFirst.length > 12 || trimmedLast.length > 12) {
            setFormError("First and Last names must be 12 characters or fewer.");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedEmail)) {
            setFormError("Please enter a valid email address.");
            return;
        }

        setFormError("");
        setCurrentStep(3);
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setFormError("");

        if (!password || !confirmPassword) {
            setFormError("Please enter and confirm your password.");
            return;
        }

        if (password.length < 8) {
            setFormError("Password must be at least 8 characters long.");
            return;
        }

        if (password !== confirmPassword) {
            setFormError("Passwords do not match.");
            return;
        }

        if (!termsAgreed) {
            setFormError("Please agree to the Terms of Service to continue.");
            return;
        }

        setLoading(true);

        const dob = `${dobMonth.padStart(2, '0')}/${dobDay.padStart(2, '0')}/${dobYear}`;
        const selectedLanguageNames = selections
            .map((selected, idx) => (selected ? languages[idx].name : ""))
            .filter(Boolean)
            .join(",");

        const formData = {
            password: password,
            "first-name": firstName.trim(),
            "last-name": lastName.trim(),
            email: email.trim(),
            dob: dob,
            languages: selectedLanguageNames
        };

        try {
            const response = await authFetch(`${API_BASE_URL}/api/new-user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok) {
                login({
                    id: data.user_id,
                    email: data.email,
                    is_admin: Boolean(data.is_admin)
                }, data.token);
                navigate('/dashboard');
            } else {
                setFormError(data.error || 'Failed to create account. Please try again.');
                setFormErrorCode(data.code || '');
                setLoading(false);
            }
        } catch (err) {
            console.error('Sign up error:', err);
            setFormError('Could not connect to the server. Please check your connection.');
            setFormErrorCode('');
            setLoading(false);
        }
    };

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 100 }, (_, i) => currentYear - 10 - i);

    return (
        <div className="body">
            <div className="get-started-page">
                <div className="question-container">
                    {/* Visual Stepper */}
                    <div className="signup-stepper" aria-label="Sign up steps">
                        <div className={`stepper-item ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
                            <div className="stepper-circle">
                                {currentStep > 1 ? (
                                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                ) : (
                                    "1"
                                )}
                            </div>
                            <span className="stepper-title">Language</span>
                        </div>

                        <div className={`stepper-divider ${currentStep >= 2 ? 'filled' : ''}`} />

                        <div className={`stepper-item ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
                            <div className="stepper-circle">
                                {currentStep > 2 ? (
                                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                    </svg>
                                ) : (
                                    "2"
                                )}
                            </div>
                            <span className="stepper-title">Profile</span>
                        </div>

                        <div className={`stepper-divider ${currentStep >= 3 ? 'filled' : ''}`} />

                        <div className={`stepper-item ${currentStep >= 3 ? 'active' : ''}`}>
                            <div className="stepper-circle">3</div>
                            <span className="stepper-title">Security</span>
                        </div>
                    </div>

                    {/* STEP 1: Select Languages */}
                    {currentStep === 1 && (
                        <div className="step-panel first" key="step-1">
                            <h1 className="step-heading">What would you like to learn?</h1>
                            <p className="step-subheading">Select one or more languages</p>

                            <div className="flag-form">
                                {languages.map((language) => {
                                    const isSelected = selections[language.id];
                                    return (
                                        <div className="flag" key={language.id}>
                                            <button
                                                type="button"
                                                className={`flag-card ${isSelected ? 'selected' : ''}`}
                                                aria-pressed={isSelected}
                                                data-selected={isSelected}
                                                onClick={() => toggleLanguage(language.id)}
                                            >
                                                <div className="flag-card-img-wrap">
                                                    <img
                                                        src={language.image}
                                                        alt={`${language.displayName} Flag`}
                                                        className="flag-card-img"
                                                    />
                                                    {isSelected && (
                                                        <div className="flag-card-check" aria-hidden="true">
                                                            <svg viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flag-card-text-wrap">
                                                    <p className="flag-card-name">{language.displayName}</p>
                                                </div>
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            {formError && (
                                <div className="formerror" role="alert">
                                    <svg className="error-icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <span>{formError}</span>
                                </div>
                            )}

                            <div className="step-actions">
                                <button className="next" type="button" onClick={handleFirstNext}>
                                    Next
                                    <span className="btn-arrow" aria-hidden="true">→</span>
                                </button>
                            </div>

                            <div className="login-prompt-container">
                                Already have an account?{' '}
                                <Link to="/login" className="login-prompt-link">
                                    Log In
                                </Link>
                            </div>
                        </div>
                    )}

                    {/* STEP 2: Personal Profile */}
                    {currentStep === 2 && (
                        <div className="step-panel second" id="second" key="step-2">
                            <h1 className="step-heading">Welcome!</h1>
                            <p className="step-subheading">Tell us a little bit about yourself</p>

                            <form className="get-started-form" onSubmit={handleSecondNext}>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="fname">First Name</label>
                                    <input
                                        type="text"
                                        id="fname"
                                        name="fname"
                                        className="form-input"
                                        placeholder="e.g. Alex"
                                        maxLength={12}
                                        value={firstName}
                                        onChange={(e) => {
                                            setFirstName(e.target.value);
                                            setFormError("");
                                        }}
                                        autoComplete="given-name"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label" htmlFor="lname">Last Name</label>
                                    <input
                                        type="text"
                                        id="lname"
                                        name="lname"
                                        className="form-input"
                                        placeholder="e.g. Morgan"
                                        maxLength={12}
                                        value={lastName}
                                        onChange={(e) => {
                                            setLastName(e.target.value);
                                            setFormError("");
                                        }}
                                        autoComplete="family-name"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label" htmlFor="email">Email Address</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        className="form-input"
                                        placeholder="alex@example.com"
                                        value={email}
                                        onChange={(e) => {
                                            setEmail(e.target.value);
                                            setFormError("");
                                        }}
                                        autoComplete="email"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label" htmlFor="dob-day">Date of Birth</label>
                                    <div className="dob-row">
                                        <select
                                            id="dob-day"
                                            name="dob-day"
                                            className="dob-select"
                                            value={dobDay}
                                            onChange={(e) => {
                                                setDobDay(e.target.value);
                                                setFormError("");
                                            }}
                                            required
                                            aria-label="Day of birth"
                                        >
                                            <option value="">DD</option>
                                            {[...Array(31)].map((_, i) => {
                                                const d = String(i + 1);
                                                return <option key={d} value={d}>{d}</option>;
                                            })}
                                        </select>

                                        <select
                                            id="dob-month"
                                            name="dob-month"
                                            className="dob-select"
                                            value={dobMonth}
                                            onChange={(e) => {
                                                setDobMonth(e.target.value);
                                                setFormError("");
                                            }}
                                            required
                                            aria-label="Month of birth"
                                        >
                                            <option value="">MM</option>
                                            {months.map((m) => (
                                                <option key={m.value} value={m.value}>{m.name}</option>
                                            ))}
                                        </select>

                                        <select
                                            id="dob-year"
                                            name="dob-year"
                                            className="dob-select"
                                            value={dobYear}
                                            onChange={(e) => {
                                                setDobYear(e.target.value);
                                                setFormError("");
                                            }}
                                            required
                                            aria-label="Year of birth"
                                        >
                                            <option value="">YYYY</option>
                                            {yearOptions.map((year) => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {formError && (
                                    <div className="formerror" role="alert">
                                        <svg className="error-icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <span>
                                            {formError}
                                            {formErrorCode === 'email_already_exists' && (
                                                <Link to="/login" style={{ marginLeft: '8px', color: '#4f46e5', fontWeight: '600', textDecoration: 'underline' }}>
                                                    Log in
                                                </Link>
                                            )}
                                        </span>
                                    </div>
                                )}

                                <div className="buttonBar">
                                    <button
                                        className="back"
                                        type="button"
                                        onClick={() => {
                                            setFormError("");
                                            setCurrentStep(1);
                                        }}
                                    >
                                        <span className="btn-arrow" aria-hidden="true">←</span>
                                        Back
                                    </button>
                                    <button className="next" type="submit">
                                        Next
                                        <span className="btn-arrow" aria-hidden="true">→</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* STEP 3: Password & Terms */}
                    {currentStep === 3 && (
                        <div className="step-panel third" key="step-3">
                            <h1 className="step-heading">Create your password</h1>
                            <p className="step-subheading">Choose a secure password with at least 8 characters. <br />DO NOT FORGET YOUR PASSWORD.</p>

                            <form className="get-started-form" onSubmit={handlePasswordSubmit}>
                                <div className="form-group">
                                    <label className="form-label" htmlFor="password">Password</label>
                                    <div className="input-with-toggle">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            id="password"
                                            name="password"
                                            className="form-input"
                                            placeholder="••••••••"
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                setFormError("");
                                            }}
                                            autoComplete="new-password"
                                            required
                                            minLength={8}
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle-btn"
                                            onClick={() => setShowPassword(!showPassword)}
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                        >
                                            {showPassword ? "Hide" : "Show"}
                                        </button>
                                    </div>
                                    <span className="field-hint">Must be at least 8 characters</span>
                                </div>

                                <div className="form-group">
                                    <label className="form-label" htmlFor="confirmPassword">Confirm Password</label>
                                    <div className="input-with-toggle">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            id="confirmPassword"
                                            name="confirmPassword"
                                            className="form-input"
                                            placeholder="••••••••"
                                            value={confirmPassword}
                                            onChange={(e) => {
                                                setConfirmPassword(e.target.value);
                                                setFormError("");
                                            }}
                                            autoComplete="new-password"
                                            required
                                            minLength={8}
                                        />
                                        <button
                                            type="button"
                                            className="password-toggle-btn"
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            aria-label={showConfirmPassword ? "Hide password confirmation" : "Show password confirmation"}
                                        >
                                            {showConfirmPassword ? "Hide" : "Show"}
                                        </button>
                                    </div>
                                </div>

                                <div className="terms-checkbox-container">
                                    <label className="terms-checkbox-label" htmlFor="terms-checkbox">
                                        <input
                                            type="checkbox"
                                            id="terms-checkbox"
                                            checked={termsAgreed}
                                            onChange={(e) => {
                                                setTermsAgreed(e.target.checked);
                                                if (e.target.checked && formError === 'Please agree to the Terms of Service to continue.') {
                                                    setFormError('');
                                                }
                                            }}
                                            required
                                        />
                                        <span>
                                            I agree to the{' '}
                                            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
                                                Terms of Service
                                            </a>{' '}
                                            and understand this app is a prototype provided AS-IS.
                                        </span>
                                    </label>
                                </div>

                                {formError && (
                                    <div className="formerror" role="alert">
                                        <svg className="error-icon" viewBox="0 0 20 20" fill="currentColor" width="16" height="16" aria-hidden="true">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <span>
                                            {formError}
                                            {formErrorCode === 'email_already_exists' && (
                                                <Link to="/login" style={{ marginLeft: '8px', color: '#4f46e5', fontWeight: '600', textDecoration: 'underline' }}>
                                                    Log in
                                                </Link>
                                            )}
                                        </span>
                                    </div>
                                )}

                                <div className="buttonBar">
                                    <button
                                        className="back"
                                        type="button"
                                        disabled={loading}
                                        onClick={() => {
                                            setFormError("");
                                            setCurrentStep(2);
                                        }}
                                    >
                                        <span className="btn-arrow" aria-hidden="true">←</span>
                                        Back
                                    </button>
                                    <button className="next" type="submit" disabled={loading}>
                                        {loading ? 'Creating Account...' : 'Create Account'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default GetStarted;
